import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { theme } from 'lib/style';
import { MdOutlineUnfoldLess, MdOutlineUnfoldMore } from 'react-icons/md';
import { StatusBadge } from 'react-covideo-common';

const Wrapper = styled.div<{ disabled: boolean }>`
  padding: 16px;
  background: ${theme.palette.white};
  border: 1px solid ${theme.palette.blue02};
  box-shadow:
    0px 0px 2px rgba(66, 79, 104, 0.08),
    0px 4px 8px rgba(66, 79, 104, 0.03);
  border-radius: 6px;
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div<{ disabled: boolean }>`
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${theme.palette.blue100};
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.2;
    `}
`;

const Expander = styled.div<{ disabled: boolean }>`
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.2;
    `}
`;

const Content = styled.div<{ show: boolean }>`
  margin-top: 16px;
  display: ${({ show }) => (show ? `block` : `none;`)};
`;

type Props = {
  children?: React.ReactNode;
  title: string;
  expandedDefault?: boolean;
  disabled?: boolean;
  badgeStatus?: undefined | 'success' | 'danger' | 'default';
  badgeText?: string;
  onExpand?: () => void;
};

export const StepCard = ({
  children = null,
  title,
  expandedDefault = false,
  badgeStatus,
  badgeText,
  disabled = false,
  onExpand,
}: Props) => {
  const [expanded, setExpanded] = useState(expandedDefault);
  const onChangeExpanded = () => {
    onExpand?.();
    setExpanded(prevExpanded => !prevExpanded);
  };
  const getBadgeVariant = (badgeStatus: string | undefined) => {
    switch (badgeStatus) {
      case 'success':
        return 'success';

      case 'danger':
        return 'error';

      default:
        return 'neutral';
    }
  };

  const variant = getBadgeVariant(badgeStatus);
  return (
    <Wrapper disabled={disabled}>
      <Header>
        <Title disabled={disabled}>{title}</Title>
        {badgeText && (
          <StatusBadge variant={variant} text={badgeText} size='small' />
        )}
        <Expander disabled={disabled} onClick={() => onChangeExpanded()}>
          {expanded && (
            <MdOutlineUnfoldLess color={theme.palette.covideoBlue40} />
          )}
          {!expanded && (
            <MdOutlineUnfoldMore color={theme.palette.covideoBlue40} />
          )}
        </Expander>
      </Header>
      {children && <Content show={expanded}>{children}</Content>}
    </Wrapper>
  );
};
